import React, { createContext, useState, ReactNode, useEffect } from 'react';

interface UserDataFromApi {
    id: number;
    grupo: number;
    token: string;
    nombre: string;
    apellido: string;
    email: string;
    activo: boolean;
    role?: string;
}

interface AppState {
    basename: string | undefined;
    apiEndpoint: string | undefined;
    theme: string;
    language: string;
    userRole: string;
}

interface MyContextType {
    state: {
        basename: string | undefined;
        apiEndpoint: string | undefined;
        theme: string;
        language: string;
        userRole: string;
    };
    isAuthenticated: boolean;
    userData: UserDataFromApi | null;
    login: (userData: UserDataFromApi) => void;
    logout: () => void;
    setIsAuthenticated: React.Dispatch<React.SetStateAction<boolean>>;
    setUserData: React.Dispatch<React.SetStateAction<UserDataFromApi | null>>;
}

const defaultState: AppState = {
    basename: process.env.REACT_APP_BASENAME,
    apiEndpoint: process.env.REACT_APP_API_URL,
    theme: 'light',
    language: 'es',
    userRole: 'guest',
};

const SESSION_EXPIRATION_TIME = 15 * 60 * 1000; // 15 minutes

export const MyContext = createContext<MyContextType | undefined>(undefined);

export const MyProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [state] = useState<AppState>(defaultState);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [userData, setUserData] = useState<UserDataFromApi | null>(null);
    console.log('UserData', userData);

    const determineUserRole = (grupoId: number): string => {
        switch(grupoId) {
            case 1: return 'admin';
            case 2: return 'editor';
            case 3: return 'viewer';
            default: return 'user';
        }
    };

    useEffect(() => {
        const initializeAuth = () => {
            const savedAuth = localStorage.getItem('isAuthenticated');
            const timestamp = localStorage.getItem('authTimestamp');
            const savedUserData = localStorage.getItem('userData');
            const savedToken = localStorage.getItem('authToken');

            if (savedAuth === 'true' && timestamp && savedUserData && savedToken) {
                const currentTime = Date.now();
                const savedTime = parseInt(timestamp, 10);

                if (currentTime - savedTime < SESSION_EXPIRATION_TIME) {
                    try {
                        const parsedUserData = JSON.parse(savedUserData) as UserDataFromApi;

                        if (!isTokenExpired(savedToken)) {
                            setIsAuthenticated(true);
                            setUserData({
                                ...parsedUserData,
                                token: savedToken
                            });
                        } else {
                            clearAuthData();
                        }
                    } catch (e) {
                        console.error("Error parsing user data", e);
                        clearAuthData();
                    }
                } else {
                    clearAuthData();
                }
            }
        };

        initializeAuth();
    }, []);

    const isTokenExpired = (token: string): boolean => {
        try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            return payload.exp * 1000 < Date.now();
        } catch {
            return true;
        }
    };

    const clearAuthData = () => {
        localStorage.removeItem('isAuthenticated');
        localStorage.removeItem('authTimestamp');
        localStorage.removeItem('userData');
        localStorage.removeItem('authToken');
        setIsAuthenticated(false);
        setUserData(null);
    };

    const login = (data: UserDataFromApi) => {
        const userRole = determineUserRole(data.grupo);

        const completeUserData = {
            ...data,
            role: userRole
        };

        setIsAuthenticated(true);
        setUserData(completeUserData);
        localStorage.setItem('isAuthenticated', 'true');
        localStorage.setItem('authTimestamp', Date.now().toString());
        localStorage.setItem('userData', JSON.stringify(completeUserData));
        localStorage.setItem('authToken', data.token);

        // Update the role in the global state
        state.userRole = userRole;
    };

    const logout = () => {
        clearAuthData();
        window.location.href = '/';
    };

    return (
        <MyContext.Provider value={{
            state,
            isAuthenticated,
            userData,
            login,
            logout,
            setIsAuthenticated,
            setUserData
        }}>
            {children}
        </MyContext.Provider>
    );
};